// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2025 Vic Zhu <viczhu2014@gmail.com>

/**
 * Utility functions for calculating dynamic pagination limits based on data characteristics
 */

// CGM data characteristics
const READINGS_PER_HOUR = 20 // 3-minute intervals
const READINGS_PER_DAY = READINGS_PER_HOUR * 24 // 480 readings per day

// Safety limits
const MIN_PER_PAGE = 1000
const MAX_PER_PAGE = 50000 // Conservative max to prevent server overload
const SAFETY_BUFFER_MULTIPLIER = 1.2 // 20% buffer for data gaps

/**
 * The perPage EVERY windowed metrics query on the pet-detail panel must use.
 *
 * Those widgets all describe the same pet over the same window and receive the
 * same rows — but a react-query key includes `perPage`, so asking for
 * 10000 / 20000 / 50000 / days×480×1.2 produced SIX DIFFERENT KEYS for
 * identical data, and six separate fetches. Measured on one PetShow load
 * (2 devices, 5,832 readings): six substantive metrics responses, exactly ONE
 * distinct (docs, firstTs, lastTs) signature, 52 MB of metrics traffic and
 * 11.2s of server time — ~41 MB and ~9s of it pure duplication (eng-brain
 * P17-1).
 *
 * 20000 is the server's own cap (METRICS_MAX_LIMIT, ada-core
 * Metrics/index.ts), so no window can return more and asking for it costs
 * nothing — the `timestamp` bounds already limit the payload. One constant also
 * means every widget computes its statistics over IDENTICAL rows, closing the
 * pass-13 divergence where adjacent cards could disagree for a pet with >10000
 * readings in-window.
 *
 * Do NOT replace this with a per-widget or window-derived value: any divergence
 * silently re-splits the cache and the duplication comes straight back.
 */
export const SHARED_METRICS_PER_PAGE = 20000

/**
 * Calculate optimal perPage value based on date range for CGM metrics
 *
 * @param startDate - Start date of the range
 * @param endDate - End date of the range
 * @param bufferMultiplier - Optional buffer multiplier (default: 1.2 for 20% buffer)
 * @returns Optimal perPage value capped at MAX_PER_PAGE
 *
 * @example
 * // For 14 days: ~6,720 readings + 20% buffer = ~8,064
 * const perPage = calculateMetricsPagination(startDate, endDate);
 */
export function calculateMetricsPagination(
  startDate: Date,
  endDate: Date,
  bufferMultiplier: number = SAFETY_BUFFER_MULTIPLIER,
): number {
  // Calculate days in range
  const timeDiff = endDate.getTime() - startDate.getTime()
  const daysInRange = Math.ceil(timeDiff / (24 * 60 * 60 * 1000))

  // Calculate estimated readings
  const estimatedReadings = daysInRange * READINGS_PER_DAY

  // Apply buffer and cap
  const perPageWithBuffer = Math.ceil(estimatedReadings * bufferMultiplier)
  const cappedPerPage = Math.min(Math.max(perPageWithBuffer, MIN_PER_PAGE), MAX_PER_PAGE)

  return cappedPerPage
}

/**
 * Get default pagination for metrics when no date range is specified
 * Defaults to 30 days worth of data
 *
 * @returns Default perPage value for metrics
 */
export function getDefaultMetricsPagination(): number {
  const thirtyDays = 30
  return Math.min(Math.ceil(thirtyDays * READINGS_PER_DAY * SAFETY_BUFFER_MULTIPLIER), MAX_PER_PAGE)
}

/**
 * Calculate pagination info for debugging
 *
 * @param startDate - Start date of the range
 * @param endDate - End date of the range
 * @returns Object with pagination details
 */
export function getPaginationInfo(startDate: Date, endDate: Date) {
  const timeDiff = endDate.getTime() - startDate.getTime()
  const daysInRange = Math.ceil(timeDiff / (24 * 60 * 60 * 1000))
  const estimatedReadings = daysInRange * READINGS_PER_DAY
  const perPage = calculateMetricsPagination(startDate, endDate)

  return {
    daysInRange,
    readingsPerDay: READINGS_PER_DAY,
    estimatedReadings,
    perPage,
    willBeComplete: estimatedReadings <= perPage,
  }
}
